// Mock data for E.A. Lead Solutions clone
// NOTE: This data is currently MOCKED on the frontend. Form submissions are stored
// in localStorage until backend (MongoDB + email) integration is wired up.

import {
  Wrench, Zap, Paintbrush, Home as HomeIcon, Factory, Snowflake,
  ShieldCheck, FileText, Clock, Star, Phone, Mail, MapPin,
  Smartphone, Banknote, CalendarClock, LifeBuoy, Users, ScanFace, Umbrella
} from 'lucide-react';

export const COMPANY = {
  name: 'E.A. Lead Solutions',
  legal: 'A Trading Name of E.A. Business Goals Limited',
  phone: '07943 276411',
  phoneIntl: '+44 7943276411',
  whatsapp: '447943276411',
  whatsappMsg: 'Hello, I would like to enquire about your services.',
  whatsappHours: '9am–5pm',
  email: 'general@ealeadsolutions.co.uk',
  address: '22 Royal Victoria Place, London E16 1UQ',
  companyNumber: '16302746',
  logo: '/logo.png',
};

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Our Services', to: '/our-services' },
  { label: 'About Us', to: '/about-us' },
  { label: 'Request a Service', to: '/request-a-service' },
  { label: 'Join Our Team', to: '/join-our-team' },
  { label: 'Contact Us', to: '/contact' },
];

export const SERVICES = [
  {
    id: 'plumbing',
    icon: Wrench,
    title: 'Plumbing',
    short: 'Leak repairs, pipe installations, boiler servicing, bathroom fitting, and emergency plumbing — fast and reliable.',
    heading: 'Plumbing Services',
    blurb: 'From dripping taps to full bathroom installations, our certified plumbers handle it all.',
    points: [
      'Leak detection and repair',
      'Pipe installation and replacement',
      'Boiler servicing and repair',
      'Bathroom and kitchen fitting',
      'Drain unblocking and maintenance',
      'Emergency plumbing (same-day response)',
    ],
  },
  {
    id: 'electrical',
    icon: Zap,
    title: 'Electrical',
    short: 'Wiring, socket installations, fuse board upgrades, lighting, safety inspections, and fault finding.',
    heading: 'Electrical Services',
    blurb: 'Qualified electricians for residential and light commercial work, fully certified and insured.',
    points: [
      'Full and partial rewiring',
      'Socket and switch installation',
      'Fuse board / consumer unit upgrades',
      'Lighting design and installation',
      'Electrical safety inspections (EICR)',
      'Fault finding and diagnostics',
    ],
  },
  {
    id: 'painting',
    icon: Paintbrush,
    title: 'Painting & Decorating',
    short: 'Interior and exterior painting, wallpapering, feature walls, and full property refreshes to the highest standard.',
    heading: 'Painting & Decorating',
    blurb: 'Transform your space with our professional painters and decorators.',
    points: [
      'Interior and exterior painting',
      'Wallpaper hanging and removal',
      'Feature walls and accent colours',
      'Wood staining and varnishing',
      'Full property refreshes (landlord specials)',
      'Colour consultation available',
    ],
  },
  {
    id: 'house-cleaning',
    icon: HomeIcon,
    title: 'House Cleaning',
    short: 'Regular cleaning, deep cleans, end-of-tenancy, move-in/move-out cleans, and one-off spring cleans.',
    heading: 'House Cleaning',
    blurb: 'From regular upkeep to deep cleans, we leave your home spotless.',
    points: [
      'Regular weekly / fortnightly cleaning',
      'Deep cleaning and sanitisation',
      'End-of-tenancy cleaning',
      'Move-in / move-out cleans',
      'Spring cleaning',
      'Oven and appliance cleaning',
    ],
  },
  {
    id: 'commercial-cleaning',
    icon: Factory,
    title: 'Commercial Cleaning',
    short: 'Factory floors, construction site clean-ups, office deep cleans, and post-build cleaning services.',
    heading: 'Commercial & Construction Cleaning',
    blurb: 'Specialist cleaning for factories, construction sites, and commercial properties.',
    points: [
      'Post-construction site clean-ups',
      'Factory and warehouse floor cleaning',
      'Office deep cleans',
      'Window cleaning (internal)',
      'Waste removal coordination',
      'Regular commercial maintenance contracts',
    ],
  },
  {
    id: 'hvac',
    icon: Snowflake,
    title: 'HVAC',
    short: 'Heating, ventilation, and air conditioning installation, maintenance, servicing, and emergency repairs.',
    heading: 'HVAC Services',
    blurb: 'Keep your property comfortable year-round with our heating, ventilation, and air conditioning experts.',
    points: [
      'Air conditioning installation and servicing',
      'Central heating installation and repair',
      'Ventilation system maintenance',
      'Thermostat and smart heating setup',
      'Gas safety certificates',
      'Emergency heating repairs',
    ],
  },
];

export const HOW_IT_WORKS = [
  { step: 1, title: 'Post a Job', text: 'Tell us what you need. Create a free account and post your job in minutes with your budget and timeline.' },
  { step: 2, title: 'Receive Bids', text: 'Verified professionals in your area review your job and submit their best quotes directly to you.' },
  { step: 3, title: 'Choose Your Professional', text: 'Review profiles, ratings, certifications and bids. Select the professional you trust.' },
  { step: 4, title: 'Job Done', text: "Your chosen professional completes the work. Pay only when you're satisfied." },
];

export const WHY_CHOOSE = [
  { icon: ShieldCheck, title: 'Vetted Professionals', text: 'Every operative is background-checked, certified and insured before joining our platform.' },
  { icon: Users, title: 'You Choose', text: 'Read reviews, compare bids and select the professional YOU want — not one assigned to you.' },
  { icon: FileText, title: 'Transparent Pricing', text: "See exactly what you'll pay before anyone starts work. No hidden fees, ever." },
  { icon: ScanFace, title: 'Digital ID Verified', text: 'Every operative verifies their identity at your door using our secure Digital ID system (coming soon).' },
  { icon: Umbrella, title: 'Fully Insured', text: "All operatives carry their own public liability insurance — you're protected on every job." },
  { icon: MapPin, title: "London's Trusted Platform", text: 'Connecting London homeowners and businesses with verified tradespeople.' },
];

export const ABOUT_FEATURES = [
  { icon: Users, title: "We Don't Assign — You Choose", text: 'Unlike traditional managed services, our marketplace puts YOU in control. Browse verified professionals, read real reviews, and select who you want.' },
  { icon: ShieldCheck, title: 'Every Professional is Vetted', text: 'We verify identity, certifications, insurance and right to work before any operative can accept jobs on our platform.' },
  { icon: ScanFace, title: 'Digital Identity Verified at Your Door', text: 'Our patent-pending Digital ID system means you verify exactly who is entering your property before granting access (coming soon).' },
  { icon: FileText, title: 'Transparent Marketplace', text: 'Operatives set their own rates, clients set their own budgets. Everything agreed upfront, in writing, before work begins.' },
  { icon: Star, title: 'Real Reviews from Real Clients', text: 'Every review on our platform is from a verified client who completed a real job — no fake reviews.' },
  { icon: ShieldCheck, title: 'Protected by Technology', text: 'Our platform creates a complete digital audit trail of every job, every bid, every payment and every property entry.' },
];

export const TRADES = [
  { id: 'plumber', icon: Wrench, role: 'Plumber', join: 'Join as a', text: 'Leak repairs, installations, boiler servicing & emergency call-outs.' },
  { id: 'electrician', icon: Zap, role: 'Electrician', join: 'Join as an', text: 'Wiring, sockets, fuse boards, lighting & safety inspections.' },
  { id: 'painter', icon: Paintbrush, role: 'Painter & Decorator', join: 'Join as a', text: 'Interior & exterior painting, wallpapering & property refreshes.' },
  { id: 'house-cleaner', icon: HomeIcon, role: 'House Cleaner', join: 'Join as a', text: 'Regular cleans, deep cleans, end-of-tenancy & spring cleans.' },
  { id: 'commercial-cleaner', icon: Factory, role: 'Commercial Cleaner', join: 'Join as a', text: 'Factory floors, construction clean-ups & office deep cleans.' },
  { id: 'hvac-tech', icon: Snowflake, role: 'HVAC Technician', join: 'Join as an', text: 'Heating, ventilation & air conditioning — install, maintain & repair.' },
];

export const FREELANCER_BENEFITS = [
  { icon: Smartphone, title: 'Steady Stream of Jobs', text: 'No more chasing leads. We handle marketing and bookings — you just show up and do great work.' },
  { icon: CalendarClock, title: 'Flexible Schedule', text: 'Work when you want. Accept or decline jobs based on your availability. No minimum hours.' },
  { icon: Banknote, title: 'Competitive Rates', text: "Earn what your skills are worth. Transparent pricing — you always know what you'll be paid." },
  { icon: LifeBuoy, title: 'Support & Insurance', text: 'Public liability cover on our jobs, plus a dedicated support team whenever you need help.' },
];

export const CONTACT_INFO = [
  { icon: MapPin, text: '22 Royal Victoria Place, London E16 1UQ' },
  { icon: Smartphone, text: 'WhatsApp support: 9am–5pm' },
  { icon: Mail, text: 'general@ealeadsolutions.co.uk' },
];
